import { CartAddEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

class QuickSizeSelector extends HTMLElement {
  constructor() {
    super();
    this.toggleBtn = null;
    this.sizeList = null;
    this.inFlight = false;

    this._onToggleClick = this._onToggleClick.bind(this);
    this._onListClick = this._onListClick.bind(this);
    this._blockNav = this._blockNav.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);
  }

  connectedCallback() {
    this.toggleBtn = this.querySelector(".quick-add-size-btn");
    this.sizeList = this.querySelector(".quick-size-list");
    if (!this.toggleBtn || !this.sizeList) return;

    // toggle show/hide
    this.toggleBtn.addEventListener("click", this._onToggleClick);

    // handle size selection + ATC
    this.sizeList.addEventListener("click", this._onListClick);

    // prevent parent <a> navigation
    this.toggleBtn.addEventListener("click", this._blockNav);
    this.sizeList.addEventListener("click", (e) => {
      if (e.target.closest(".size-btn")) this._blockNav(e);
    });

    document.addEventListener("click", this._onDocumentClick);
  }

  disconnectedCallback() {
    if (!this.toggleBtn || !this.sizeList) return;
    this.toggleBtn.removeEventListener("click", this._onToggleClick);
    this.sizeList.removeEventListener("click", this._onListClick);
    this.toggleBtn.removeEventListener("click", this._blockNav);
    document.removeEventListener("click", this._onDocumentClick);
  }

  // === helpers ===
  _openSelector() {
    if (!this.toggleBtn || !this.sizeList) return;
    this.toggleBtn.classList.add("hidden");
    this.toggleBtn.setAttribute("aria-expanded", "true");
    this.sizeList.classList.remove("hidden");
    this.classList.add("open");
  }

  _closeSelector() {
    if (!this.toggleBtn || !this.sizeList) return;
    this.classList.remove("open");
    this.sizeList.classList.add("hidden");
    this.toggleBtn.classList.remove("hidden");
    this.toggleBtn.setAttribute("aria-expanded", "false");
  }

  _closeOtherSelectors() {
    document.querySelectorAll("quick-size-selector.open").forEach((el) => {
      if (el !== this) {
        if (typeof el._closeSelector === "function") {
          el._closeSelector();
          return;
        }
        el.classList.remove("open");
        el.querySelector(".quick-size-list")?.classList.add("hidden");
        const toggleButton = el.querySelector(".quick-add-size-btn");
        toggleButton?.classList.remove("hidden");
        toggleButton?.setAttribute("aria-expanded", "false");
      }
    });
  }

  _lockButtons(activeBtn) {
    const all = [...this.sizeList.querySelectorAll(".size-btn")];
    all.forEach((b) => {
      if (b !== activeBtn) b.disabled = true; // disable others
      b.classList.toggle("is-loading", b === activeBtn);
      b.setAttribute("aria-busy", b === activeBtn ? "true" : "false");
    });
    activeBtn.disabled = true; // disable clicked one too
  }

  _unlockButtons() {
    const all = [...this.sizeList.querySelectorAll(".size-btn")];
    all.forEach((b) => {
      if (!b.hasAttribute("data-perma-disabled")) b.disabled = false;
      b.classList.remove("is-loading");
      b.setAttribute("aria-busy", "false");
    });
  }

  // === events ===
  _onToggleClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const isOpening = !this.classList.contains("open");
    this._closeOtherSelectors(); // close others first

    if (isOpening) {
      this._openSelector();
    } else {
      this._closeSelector();
    }
  }

  async _onListClick(e) {
    const btn = e.target.closest(".size-btn:not([disabled])");
    if (!btn || this.inFlight) return;

    // mark active
    this.sizeList.querySelectorAll(".size-btn.active").forEach((b) =>
      b.classList.remove("active")
    );
    btn.classList.add("active");

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const qtyAttr = this.getAttribute("data-qty");
    const quantity = Number(qtyAttr || 1) || 1;

    // lock others while adding
    this.inFlight = true;
    this._lockButtons(btn);

    try {
      await this._addToCart(variantId, quantity);

      // emit events
      // this.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true }));
      // document.dispatchEvent(
      //   new CustomEvent("cart:updated", { detail: { source: "quick-size" } })
      // );

      // Optional: feedback
      // const origText = btn.textContent;
      // btn.textContent = "Added";
      // setTimeout(() => (btn.textContent = origText), 900);
    } catch (err) {
      console.error("ATC failed:", err);
      alert("Failed to add to cart. Please try again.");
    } finally {
      this.inFlight = false;
      this._unlockButtons();
    }
  }

  async _addToCart(variantId, quantity) {
    const sectionIds = this._getCartSectionIds();

    try {
      const variant = Number(variantId) || variantId;
      const formData = new FormData();
      formData.append('items[0][id]', variant);
      formData.append('items[0][quantity]', quantity ?? 1);

      if (sectionIds.length) {
        formData.append('sections', sectionIds.join(','));
      }
      formData.append('sections_url', window.location.pathname);

      const fetchCfg = fetchConfig('javascript', { body: formData });
      const res = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });

      const response = await res.json();
      if (!res.ok || response.status) {
        const message = response.message || response.description || 'Sorry, could not add to bag';
        throw new Error(message);
      }

      const sections = await this._buildSectionsPayload(sectionIds, response.sections);

      const cartRes = await fetch('/cart.js', { cache: 'no-store' });
      const cartData = await cartRes.json();
      const itemCount = cartData.item_count;

      this._emitCartUpdate({
        sectionIds,
        sections,
        variant,
        itemCount,
      });
    } catch (err) {
      console.error('Add to cart error:', err);
      throw err;
    }
  }

  _getCartSectionIds() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const ids = new Set();
    cartItemsComponents.forEach((component) => {
      const sectionId = component?.dataset?.sectionId;
      if (sectionId) ids.add(sectionId);
    });
    return Array.from(ids);
  }

  async _buildSectionsPayload(sectionIds, sections = {}) {
    const payload = sections ? { ...sections } : {};
    const missingSections = sectionIds.filter((id) => id && !payload[id]);

    if (!missingSections.length) return payload;

    await Promise.all(
      missingSections.map(async (sectionId) => {
        try {
          payload[sectionId] = await sectionRenderer.getSectionHTML(sectionId, false);
        } catch (error) {
          console.warn(`Failed to render section ${sectionId}`, error);
        }
      })
    );

    return payload;
  }

  _emitCartUpdate({ sectionIds, sections, variant, itemCount }) {
    const primarySectionId = sectionIds[0] || '';
    this.dispatchEvent(
      new CartAddEvent({}, primarySectionId, {
        didError: false,
        source: 'quick-size-selector',
        itemCount,
        productId: variant,
        sections,
      })
    );

    this._openCartDrawer();
  }

  _openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer?.hasAttribute('auto-open')) {
      cartDrawer.open?.();
    }
  }

  _onDocumentClick(e) {
    if (!this.classList.contains("open")) return;

    const card = this.closest("product-card") || this.closest(".product-card");
    if (card && card.contains(e.target)) return;

    this._closeSelector();
  }

  _blockNav(e) {
    e.preventDefault();
    e.stopPropagation();
  }
}

if (!customElements.get("quick-size-selector")) {
  customElements.define("quick-size-selector", QuickSizeSelector);
}
